import {
  Component,
  Element,
  Prop,
  State,
  Listen,
  EventEmitter,
  Event,
  h,
  Host,
  Method,
} from "@stencil/core";
import { ContextService, JwtService } from "globals/helpers";
import { Cart } from "api/model/mini-cart";
import store, { addToCart, createCart } from "@stores/cart.store";
import { AddToCartRequest } from "@apis/requests/add-to-cart-request";
import config from "../../../environment/environment-config";
import { WarningNotification } from "@apis/responses/add-to-cart-response";
import { CartUpdatedEventDetails } from "../../../frontend-kit/organisms/f-header/f-cart-flyout/f-cart-flyout-model";

type EntryToBeAdded = { code: string; quantity: number; deliveryDate?: string };

/**
 * @author lup1bg, kcr8wz
 * @date 2019-03
 * @licensor Robert Bosch GmbH, 2019
 * @version 1.0.0
 */
@Component({
  tag: "bcp-add-to-cart",
  styleUrl: "bcp-add-to-cart.scss",
})
export class BcpAddToCart {
  @Element() el: HTMLBcpAddToCartElement;

  /** Label for 'Increase quantity' button in 'add to cart modal' */
  @Prop() increaseQuantityModalLabel = "Increase quantity";

  /** Label for 'Increase quantity' message in 'add to cart modal' */
  @Prop() productPresentInCartModalLabel = "Product already present in cart.";

  /** Label for 'cancel' button in 'add to cart modal' */
  @Prop() cancelModalLabel = "Cancel";

  /** Label for 'Add new line' button in 'add to cart modal' */
  @Prop() addNewLineModalLabel = "Add new line";

  /** Label for 'Add to cart message'  in 'add to cart modal' */
  @Prop() addToCartMessageModalLabel = "Add to cart";

  /** Label for 'Quantity changed' warning message */
  @Prop() quantityChangedWarning = "Quantity changed";

  /** Code of the product that should be added to cart. */
  @Prop({ mutable: true, reflect: true }) productCode: string;

  /** Product name is used for analytics */
  @Prop() productName: string;

  /** add to cart button label */
  @Prop() cartLabel = "Add to cart";

  /** Used to Enable/Disable add to cart button */
  @Prop({ mutable: true, reflect: true }) buttonDisabled = false;

  /**
   * Initial value of quantity spinner.
   * If not set, '1' is used as a default value.
   */
  @Prop({ mutable: true, reflect: true }) initialValue = 1;

  /**
   * Minimum allowed value for quantity spinner.
   * If not set, '1' is used as a default value.
   */
  @Prop() minimumQuantity = 1;

  /**
   * Maximum allowed value for quantity spinner.
   * If not set, '99999' is used as a default value.
   */
  @Prop() maximumQuantity: number = config.cartConfig.defaultMaxQuantity;

  /**
   * Step value for quantity spinner.
   * If not set, '1' is used as a default value.
   */
  @Prop() step = 1;

  /** Title attribute for 'input quantity' */
  @Prop() inputQuantityTitle: string;

  /** Title attribute for 'increase quantity' */
  @Prop() increaseQuantityTitle: string;

  /** Title attribute for 'decrease quantity' */
  @Prop() decreaseQuantityTitle: string;

  /**
   * Used to determine which direction will flyout open.
   * Allowed values are 'up' and 'down'.
   */
  @Prop({ mutable: true }) flyoutDirection = "down";

  /** Text for new cart button. */
  @Prop() createCartLabel = "Create new cart";

  @Prop({ mutable: true, reflect: true }) quantity: number;

  /**
   * Used to determine style for button.
   * Allowed values are 'primary' and 'secondary'.
   * Default is 'primary'.
   */
  @Prop() visualType = "primary";

  @Prop() handleMultipleProducts = false;

  @Prop({ mutable: true, reflect: true }) entries: Array<EntryToBeAdded> = [];
  @Prop() resetQuantityAfterAdding = false;

  @State() carts: Cart[] = [];
  @State() loading = false;
  @State() warningNotifications: WarningNotification[];
  @State() flyoutActive = false;

  @Event() cartUpdated: EventEmitter<CartUpdatedEventDetails>;

  private quantityUpdate: HTMLBcpQuantityUpdateElement;
  private addToCartModal: HTMLBcpModalElement;
  private recentCart: Cart;
  private isMultiCartEnabled: boolean = config.cartConfig.multiCartEnabled;
  private maximumCartsAllowed: number = config.cartConfig.maximumCarts;
  private handleMultipleProductsCallback: () => void;
  private cartConfig = config.cartConfig;

  @Method()
  async registerMultipleProductsCallback(callback: () => void): Promise<void> {
    this.handleMultipleProductsCallback = callback;
  }

  @Listen("click", { target: "document" })
  onClickOutside(e): void {
    if (!this.el.contains(e.target)) {
      this.flyoutActive = false;
    }
  }

  @Listen("quantityChanged")
  onQuantityChange(ev: CustomEvent<{ quantity: number }>): void {
    this.quantity = ev.detail.quantity;
    this.entries = [{ code: this.productCode, quantity: this.quantity }];
  }

  async componentWillLoad(): Promise<void> {
    this.entries = [{ code: this.productCode, quantity: this.quantity }];
  }

  componentDidLoad() {
    this.quantity = this.quantityUpdate?.quantity ?? 1;
    this.entries = [{ code: this.productCode, quantity: this.quantity }];
  }

  private createCartWithProduct = async () => {
    if (!JwtService.isJwtValid() && !ContextService.isAnonymous()) {
      await JwtService.updateJwt();
    }
    createCart().then((cart: Cart) => this.addProductToCart({ cart }));
  };

  private handleAddToCart = async (
    cart,
    preventEntriesMerge,
    suppressModal
  ) => {
    this.recentCart = cart;
    if (this.handleMultipleProducts && this.handleMultipleProductsCallback) {
      this.handleMultipleProductsCallback();
    }
    if (!suppressModal && this.shouldDisplayModal(preventEntriesMerge)) {
      (this.addToCartModal as any).show = true;
    } else {
      const updatedEntries = [];
      try {
        addToCart(
          new AddToCartRequest(cart, this.entries, preventEntriesMerge)
        ).then(() => this.cartUpdated.emit({ cartCode: cart.code }));

        if (this.entries[0]?.quantity !== updatedEntries[0]?.quantityAdded) {
          this.warningNotifications =
            updatedEntries[0]?.warningNotifications ?? [];
        } else {
          this.warningNotifications = null;
        }
        if (this.resetQuantityAfterAdding) {
          await this.quantityUpdate?.reset(false);
        }
      } finally {
        this.flyoutActive = false;
        this.loading = false;
      }
    }
  };

  private async addProductToCart({
    cart,
    preventEntriesMerge = false,
    suppressModal = false,
  }): Promise<void> {
    if (!JwtService.isJwtValid() && !ContextService.isAnonymous()) {
      await JwtService.updateJwt();
    }
    await this.handleAddToCart(cart, preventEntriesMerge, suppressModal);
  }

  private shouldDisplayModal(preventEntriesMerge): boolean {
    const skipOverlay = this?.cartConfig?.skipEntryMergeOverlayConfirmation;

    if (!skipOverlay) {
      for (const entryToBeAdded of this.entries) {
        const cartEntry = this.recentCart?.entries?.filter(
          (cartEntry) => cartEntry.product.codes.code === entryToBeAdded?.code
        );
        if (!preventEntriesMerge && cartEntry?.length === 1) {
          return true;
        }
      }
    }
    return false;
  }

  render() {
    return (
      <Host>
        {!this.handleMultipleProducts && (
          <bcp-quantity-update
            ref={(e: HTMLBcpQuantityUpdateElement) => (this.quantityUpdate = e)}
            minimum-quantity={this.minimumQuantity}
            maximum-quantity={this.maximumQuantity}
            quantity-changed-warning={this.quantityChangedWarning}
            initialValue={this.initialValue}
            step={this.step}
            input-quantity-title={this.inputQuantityTitle}
            decrease-quantity-title={this.decreaseQuantityTitle}
            increase-quantity-title={this.increaseQuantityTitle}
          />
        )}
        <bcp-add-to-cart-error-container warnings={this.warningNotifications} />
        <button
          class={`A-Button A-Button--${
            this.visualType === "primary"
              ? "primary"
              : "secondary  u-Background--transparent"
          }
              ${this.flyoutActive ? "flyout--active" : ""} A-Button--regular `}
          disabled={this.buttonDisabled}
          onClick={() => {
            if (ContextService.isAnonymous() || !this.isMultiCartEnabled) {
              this.addProductToCart({ cart: store.carts[0] });
            } else {
              this.flyoutActive = !this.flyoutActive;
            }
          }}
        >
          <svg class="A-Icon A-Icon--add-cart" width="24" height="24">
            <use xlinkHref="#bcp-add-to-cart" />
          </svg>
          <span>{this.cartLabel}</span>
        </button>

        <div
          class={`flyout-wrapper flyout--direction-${this.flyoutDirection} ${
            this.flyoutActive ? "flyout-wrapper--active" : ""
          }`}
        >
          <div class="flyout">
            <div class="flyout__content">
              <div class="flyout__cart-list">
                {this.loading ? (
                  <bcp-activity-indicator is-inline is-absolute />
                ) : (
                  ""
                )}
                <ul>
                  {store.carts?.length > 0 &&
                    store.carts.map((cart) => (
                      <li key={cart.code}>
                        <button
                          onClick={() => {
                            this.addProductToCart({ cart });
                          }}
                        >
                          {cart.name ?? cart.code}
                        </button>
                      </li>
                    ))}
                  {store.carts.length >= this.maximumCartsAllowed ? (
                    ""
                  ) : (
                    <li>
                      <button onClick={this.createCartWithProduct}>
                        {this.createCartLabel}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <bcp-modal ref={(e: HTMLBcpModalElement) => (this.addToCartModal = e)}>
          <div class="modal--add-to-cart">
            <div class="row">
              <div class="col-xs-12">
                <h3>
                  {this?.cartConfig?.messageModalLabel ??
                    this.addToCartMessageModalLabel}
                  <svg
                    onClick={() => this.addToCartModal.hideModal()}
                    class="A-Icon A-Icon--close"
                    width="24"
                    height="24"
                  >
                    <title>Icon close</title>
                    <use xlinkHref="#close" />
                  </svg>
                </h3>
                <p>
                  {this?.cartConfig?.presentInCartModalLabel ??
                    this.productPresentInCartModalLabel}
                </p>
                <div class="modal--add-to-cart__buttons">
                  <button
                    class="A-Button A-Button--secondary u-Background--transparent A-Button--regular"
                    onClick={() => ((this.addToCartModal as any).show = false)}
                  >
                    {this?.cartConfig?.cancelModalLabel ??
                      this.cancelModalLabel}
                  </button>
                  <button
                    class="A-Button A-Button--primary A-Button--regular"
                    onClick={() => {
                      this.addProductToCart({
                        cart: this.recentCart,
                        preventEntriesMerge: false,
                        suppressModal: true,
                      });
                      (this.addToCartModal as any).show = false;
                    }}
                  >
                    {this?.cartConfig?.increaseQuantityModalLabel ??
                      this.increaseQuantityModalLabel}
                  </button>
                  <button
                    class="A-Button A-Button--primary A-Button--regular"
                    onClick={() => {
                      this.addProductToCart({
                        cart: this.recentCart,
                        preventEntriesMerge: true,
                        suppressModal: true,
                      });
                      (this.addToCartModal as any).show = false;
                    }}
                  >
                    {this?.cartConfig?.addNewLineModalLabel ??
                      this.addNewLineModalLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </bcp-modal>
      </Host>
    );
  }
}
